import type { UseSeoMetaInput } from '@unhead/vue'
import type { DocumentNode } from 'graphql'
import type { School } from '#payload-types'

import { GetSchoolBySlugDocument } from '#graphql-exports'
import { useGlobalsStore } from '@/stores/globals'

interface SchoolQueryResult {
  Schools: {
    docs: School[]
  }
}

/**
 * Looks a school up by slug, returning `null` when there isn't one.
 *
 * It deliberately does not throw. A slug that resolves to nothing is not always a 404: some
 * bare slugs were shared by several schools before they were split apart, and a request for
 * one is a link from an older email that belongs to the school finder, not an error page.
 * Callers decide — the school pages check for that case, the `?school=` prefill just skips.
 *
 * `document` swaps the query so a caller can narrow the lookup — the SA pages pass one that
 * only matches South Australian schools.
 */
export default async (
  slug: string,
  document: DocumentNode = GetSchoolBySlugDocument,
) => {
  const nuxtApp = useNuxtApp()
  const config = useRuntimeConfig()
  const globalsStore = useGlobalsStore()

  const doc = ref<School | null>(null)

  const { data } = await useAsyncQuery<SchoolQueryResult>(document, {
    slug,
    currentYear: new Date().getFullYear(),
  })

  if (data.value?.Schools?.docs.length) {
    doc.value = data.value.Schools?.docs[0]

    const seoMeta: UseSeoMetaInput = { title: doc.value.name }

    seoMeta.ogTitle = seoMeta.twitterTitle = `${seoMeta.title} | ${
      globalsStore.settings?.meta?.title || config.public.siteName
    }`

    nuxtApp.runWithContext(() => useSeoMeta(seoMeta))
  }

  return doc
}
